import React, {useCallback, useId, useMemo, useState} from 'react';

import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';


import {type ColumnAdapter, defaultColumnAdapter} from './column-adapter';
import {useUpdateDefaultSorting} from './hooks/useUpdateDefaultSorting';

import type {SearchTemplateColumn, SortRowData} from 'src/modules/advanced-search-result/typedef';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';


type Props = {
    columnAdapter?: ColumnAdapter,
    activeTemplate: UserSearchTemplate,
    columns: Record<string, string>,
    sortingDataMap: Record<string, SortRowData>,
    limitedSortColumns: string[],
    noSortColumns: string[],
    columnsMap: SearchTemplateColumn[],
    onClose: () => void,
    onDone: () => void,
    onFail: (error: string) => void,
};

const MODAL_WIDTH = 600;

const getLimitedSortMessage = (defaultColumnCaption: string, maxResult: number) =>
    `Selected field is applicable only for results with fewer than ${maxResult} entities.
${defaultColumnCaption} field will be used for larger results.`;

export const EditSortingModal = (props: Props) => {
    const {
        columnAdapter = defaultColumnAdapter,
        activeTemplate,
        columnsMap,
        sortingDataMap,
        noSortColumns,
        limitedSortColumns,
        columns,
        onClose,
        onDone,
        onFail,
    } = props;

    const effectiveSorting = columnAdapter.getSorting(activeTemplate.defaultSortColumn,
        activeTemplate.defaultSortDirection);
    const displayColumns = columnAdapter.toDisplay(activeTemplate.columns);
    const [selectedColumn, setSelectedColumn] = useState(columns[effectiveSorting.column] || '');
    const [selectedDirection, setSelectedDirection] = useState(effectiveSorting.direction);

    const {event, busy} = useUpdateDefaultSorting({onSuccess: onDone, onFail});

    const saveSorting = useCallback(async () => {
        const saveMap = Object.fromEntries(
            Object.entries(columns).map(([key, value]) => [value, key]),
        );

        await event({
            templateId: activeTemplate.templateId,
            sortColumn: saveMap[selectedColumn],
            sortDirection: selectedDirection,
        });
    }, [event, activeTemplate, selectedColumn, selectedDirection, columns]);


    const limitedSortMessage = useMemo(
        () => getLimitedSortMessage(columnsMap[0].name, MAX_SELECTED_ENTITIES),
        [columnsMap],
    );

    const sortFieldId = useId();
    const sortDirectionId = useId();

    return (
        <>
            <DialogHeader onClose={onClose}>
                Edit Default Sorting
            </DialogHeader>
            <DialogMain style={{padding: 24, maxWidth: MODAL_WIDTH}}>
                {limitedSortColumns.includes(selectedColumn) ? (
                    <Message color="info" iconProps={{type: Icons.INFO}}>
                        {limitedSortMessage}
                    </Message>
                ) : null}
                <Group direction="v">
                    Select default sorting for the layout
                    <Group>
                        <section style={{width: 320}}>
                            <Label htmlFor={sortFieldId}>Sort Field</Label>
                            <ComboBox id={sortFieldId} value={selectedColumn} onChange={setSelectedColumn}>
                                {Object.keys(displayColumns)
                                    .sort((a, b) => displayColumns[a] - displayColumns[b])
                                    .filter(columnId => columns[columnId] && !noSortColumns.includes(columns[columnId]))
                                    .map(columnId => {
                                        const column = columnsMap
                                            .find(({code}) => code === columns[columnId]);

                                        if (!column) {
                                            return <ComboboxItem key="not-found" value="" caption="Not found"/>;
                                        }

                                        return (
                                            <ComboboxItem
                                                key={column.code}
                                                meta={limitedSortColumns.includes(column.code) ? (
                                                    <span title={limitedSortMessage}>
                                                        <Icon type={Icons.INFO}/>
                                                    </span>
                                                ) : undefined}
                                                caption={column.name}
                                                value={column.code}
                                            />
                                        );
                                    })}
                            </ComboBox>
                        </section>
                        <section style={{width: 200}}>
                            <Label htmlFor={sortDirectionId}>Sort Direction</Label>
                            <ComboBox
                                id={sortDirectionId}
                                key={selectedColumn}
                                value={selectedDirection}
                                onChange={setSelectedDirection}>
                                <ComboboxItem
                                    caption={selectedColumn ? sortingDataMap[selectedColumn].ascLabel : 'Ascending'}
                                    value={SortDirection.ASC}
                                />
                                <ComboboxItem
                                    caption={selectedColumn ? sortingDataMap[selectedColumn].descLabel : 'Descending'}
                                    value={SortDirection.DESC}
                                />
                            </ComboBox>
                        </section>
                    </Group>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.PRIMARY" busy={busy} onClick={saveSorting}>
                        Save
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
